import { useState } from 'react';
import { PiArrowRight } from 'react-icons/pi';
import { Button } from '@ewf/components/Button';
import { ConnectUserWallet } from '@ewf/components/ConnectUserWallet';
import { truncateBalance, getLiquidTokenSymbol } from '@ewf/lib/utils';
import { useConnectionStore } from '@ewf/stores/connection';
import { useBalanceStore } from '@ewf/stores/balance';
import { displayBalance } from '@ewf/utils/balance-helpers';
import ewLogo from '@ewf/assets/logos/ew.svg';

export const Balance = (): JSX.Element => {
  const [isWalletOpen, setIsWalletOpen] = useState(false);
  const isConnected = useConnectionStore((state) => state.isConnected);
  const liquidBalance = useBalanceStore((state) => state.liquidBalance);
  const balanceEWX = useBalanceStore((state) => state.balanceEWX);
  const usdcBalance = useBalanceStore((state) => state.usdcBalance);
  const isLoading = useBalanceStore((state) => state.isLoading);
  const floatBalanceEWX = parseFloat(balanceEWX?.token || '0');

  const Header = (): JSX.Element => (
    <div className="mb-1 rounded-b-sm rounded-t-lg bg-gray-90 p-4 shadow-sm">
      {isConnected ? (
        <>
          <p className="mb-2 text-base text-font-subtle">Total {getLiquidTokenSymbol()} Balance</p>
          <div className="flex items-center gap-3">
            <img src={ewLogo} width={24} height={24} alt="EWT" className="inline" />
            {isLoading ? (
              <div className="block h-[24px] w-[64px] animate-pulse rounded bg-gray-80" />
            ) : (
              <span className="text-lg font-bold">
                {displayBalance(truncateBalance(liquidBalance || '0'))}
              </span>
            )}
          </div>
        </>
      ) : (
        <p className="text-base">Accounts</p>
      )}
    </div>
  );

  const WalletConnected = (): JSX.Element => (
    <>
      {isLoading ? (
        <div className="mb-2 flex h-[28px] animate-pulse items-center rounded-full bg-gray-80" />
      ) : (
        <div className="p-0">
          <div className="space-y-2 p-2">
            {/* EWX Balance */}
            <div className="group flex items-center gap-2">
              <p className="fade-in delay-50 w-[34px] text-sm text-font-subtler transition group-hover:text-brand">
                EWX
              </p>
              <div className="fade-in delay-50 flex flex-grow items-center rounded-full border-l border-gray-80 bg-gray-80 px-3 py-1 transition group-hover:border-l group-hover:border-brand group-hover:shadow-sm">
                <span className="flex-grow pt-[2px] text-right">
                  {displayBalance(truncateBalance(floatBalanceEWX))}
                </span>
              </div>
            </div>
            {/* USDC Balance */}
            <div className="group flex items-center gap-2">
              <p className="fade-in delay-50 w-[34px] text-sm text-font-subtler transition group-hover:text-[#1783ff]">
                USDC
              </p>
              <div className="fade-in delay-50 flex flex-grow items-center rounded-full border-l border-gray-80 bg-gray-80 px-3 py-1 transition group-hover:border-l group-hover:border-[#1783ff] group-hover:shadow-sm">
                <span className="flex-grow pt-[2px] text-right">
                  {displayBalance(truncateBalance(usdcBalance || '0'))}
                </span>
              </div>
            </div>
          </div>
          <a
            href="https://staking.energywebx.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-between gap-2 rounded-[14px] px-2 py-2 transition-colors duration-300 hover:bg-violet-darker"
          >
            <div className="text-sm text-font-subtle">Get {getLiquidTokenSymbol()}</div>
            <div className="rounded-lg bg-brand p-1 text-white">
              <PiArrowRight className="size-3" />
            </div>
          </a>
        </div>
      )}
    </>
  );

  const WalletDisconnected = (): JSX.Element => (
    <div className="p-2">
      <Button
        onClick={() => setIsWalletOpen(true)}
        size="small"
        className="w-full bg-button-gradient"
      >
        Connect Wallet
      </Button>
    </div>
  );

  return (
    <div className="w-full">
      <Header />
      <div className="rounded-b-lg rounded-t-sm bg-gray-90 p-1 shadow-sm">
        {isConnected && !isWalletOpen ? <WalletConnected /> : <WalletDisconnected />}
      </div>
      <ConnectUserWallet isOpen={isWalletOpen} setIsOpen={setIsWalletOpen} />
    </div>
  );
};
